import React from 'react';
import { Hours, Weekday, WEEKDAYS } from '../../../../lib/schema/dealer/types';
import Toggle from '../../../atoms/Toggle/Toggle';

export const workdayHours = { startTime: '09:00', endTime: '17:00' };

export const defaultHours: Hours = {
  Monday: workdayHours,
  Tuesday: workdayHours,
  Wednesday: workdayHours,
  Thursday: workdayHours,
  Friday: workdayHours,
  Saturday: workdayHours,
  Sunday: null,
};

const to12Hr = (t: string) => {
  const [hourString, minute] = t.split(':');
  const hour = Number(hourString) % 24;
  return `${hour % 12 || 12}:${minute}${hour < 12 ? 'am' : 'pm'}`;
};

const dayRanges = (hours: Hours) => {
  const ranges: {
    start: Weekday;
    end: Weekday;
    time: { startTime: string; endTime: string } | null;
  }[] = [];

  WEEKDAYS.forEach((day) => {
    if (ranges.length === 0) {
      ranges.push({ start: day, end: day, time: hours[day] });
      return;
    }

    const lastRange = ranges[ranges.length - 1];

    const sameHours =
      lastRange.time?.startTime === hours[day]?.startTime &&
      lastRange.time?.endTime === hours[day]?.endTime;
    if (sameHours) {
      lastRange.end = day;
    } else {
      ranges.push({ start: day, end: day, time: hours[day] });
    }
  });

  return ranges;
};

interface ReadonlyHoursProps {
  hours: Hours;
}

export const ReadonlyHours: React.FC<ReadonlyHoursProps> = ({ hours }) => {
  return (
    <div className="flex flex-col gap-xs">
      Hours
      <ul>
        {dayRanges(hours).map((range) => (
          <li key={range.start} className="flex justify-between gap-s">
            <div>
              {range.start}
              {range.start !== range.end ? ` - ${range.end}` : ''}
            </div>
            <div>
              {range.time
                ? ` ${to12Hr(range.time.startTime)} - ${to12Hr(range.time.endTime)}`
                : ' Closed'}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

interface EditableHoursProps {
  hours: Hours;
  setHours: (hours: Hours) => void;
}

export const EditableHours: React.FC<EditableHoursProps> = ({
  hours,
  setHours,
}) => {
  return (
    <table className="w-fit-content text-body2Light text-neutral-900">
      <thead>
        <tr>
          <th>Day</th>
          <th>Open</th>
          {Object.values(hours).some((h) => h != null) ? (
            <>
              <th>Open at</th>
              <th>Closed at</th>
            </>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {WEEKDAYS.map((day) => {
          const dayHours = hours[day];
          return (
            <tr key={day} className="[&>*:not(:last-child)]:pr-xl">
              <td>{day}: </td>
              <td className="flex items-center gap-xs">
                <Toggle
                  aria-label={`toggle ${day} open`}
                  checked={!!dayHours}
                  onCheckedChange={(checked) =>
                    setHours({ ...hours, [day]: checked ? workdayHours : null })
                  }
                />
                {dayHours ? 'Open' : 'Closed'}
              </td>
              {dayHours && (
                <>
                  <td>
                    <input
                      className="cursor-pointer"
                      aria-label={`${day} start time`}
                      type="time"
                      value={dayHours.startTime}
                      onChange={(e) =>
                        setHours({
                          ...hours,
                          [day]: { ...dayHours, startTime: e.target.value },
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="cursor-pointer"
                      aria-label={`${day} end time`}
                      type="time"
                      value={dayHours.endTime}
                      onChange={(e) => {
                        setHours({
                          ...hours,
                          [day]: { ...dayHours, endTime: e.target.value },
                        });
                      }}
                    />
                  </td>
                </>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
